import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CopyButton,
  BrandedExportActions,
  EmptyHint,
  ListInput,
  Panel,
  ResetButton,
  usePersistentState,
} from "@/components/tools/ToolKit";
import {
  DEFAULT_CLASS_LIST,
  chunkIntoGroups,
  dedupe,
  groupsBySize,
  parseList,
  shuffle,
} from "@/lib/random-engine";
import { Users, Download, FileSpreadsheet, RotateCcw } from "lucide-react";
import { toast } from "sonner";

type Mode = "count" | "size";

export function TeamGenerator({
  storageKey,
  unitLabel = "Team",
  pairMode = false,
}: {
  storageKey: string;
  unitLabel?: string;
  pairMode?: boolean;
}) {
  const [raw, setRaw] = usePersistentState(storageKey, DEFAULT_CLASS_LIST.join("\n"));
  const [mode, setMode] = useState<Mode>("count");
  const [count, setCount] = useState(3);
  const [size, setSize] = useState(4);
  const [groups, setGroups] = useState<string[][]>([]);
  const [error, setError] = useState<string | null>(null);

  const names = dedupe(parseList(raw));

  const generate = () => {
    if (names.length < 2) {
      setError("Add at least 2 names to generate teams.");
      return;
    }
    const shuffled = shuffle(names);
    if (pairMode) {
      const pairs: string[][] = [];
      for (let i = 0; i < shuffled.length; i += 2) pairs.push(shuffled.slice(i, i + 2));
      if (pairs.length > 1 && pairs[pairs.length - 1]!.length === 1) {
        const last = pairs.pop()!;
        pairs[pairs.length - 1]!.push(last[0]!);
      }
      setGroups(pairs);
    } else if (mode === "count") {
      if (count < 1) {
        setError("Please enter a valid number of teams.");
        return;
      }
      setGroups(chunkIntoGroups(shuffled, Math.min(count, shuffled.length)));
    } else {
      if (size < 1) {
        setError("Please enter a valid team size.");
        return;
      }
      setGroups(groupsBySize(shuffled, size));
    }
    setError(null);
  };

  const asText = groups
    .map((g, i) => `${pairMode ? "Pair" : unitLabel} ${i + 1} (${g.length} Members):\n  • ${g.join("\n  • ")}`)
    .join("\n\n");

  // Download Plain Text Roster
  const downloadTxt = () => {
    const header = `========================================\nCLASS ${unitLabel.toUpperCase()} ROSTER\nGenerated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}\nTotal Students: ${names.length} | Total ${unitLabel}s: ${groups.length}\n========================================\n\n`;
    const content = header + asText;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${unitLabel.toLowerCase()}-roster-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded TXT Roster");
  };

  // Download Excel-Compatible CSV
  const downloadCsv = () => {
    let csv = `${unitLabel} Name,Member Number,Student Name\n`;
    groups.forEach((g, groupIdx) => {
      const groupName = `${pairMode ? "Pair" : unitLabel} ${groupIdx + 1}`;
      g.forEach((member, memberIdx) => {
        csv += `"${groupName}","${memberIdx + 1}","${member.replace(/"/g, '""')}"\n`;
      });
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${unitLabel.toLowerCase()}-teams-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded CSV Sheet");
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
      {/* Settings Panel */}
      <Panel title="Roster & Setup" className="no-print space-y-4">
        <ListInput value={raw} onChange={setRaw} label="Student Names" count={names.length} />

        {!pairMode ? (
          <div className="space-y-3 pt-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Grouping Rule
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                type="button"
                size="sm"
                variant={mode === "count" ? "default" : "outline"}
                onClick={() => setMode("count")}
                className="text-xs"
              >
                By {unitLabel} Count
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === "size" ? "default" : "outline"}
                onClick={() => setMode("size")}
                className="text-xs"
              >
                By {unitLabel} Size
              </Button>
            </div>

            {mode === "count" ? (
              <label className="block text-sm font-medium">
                Number of {unitLabel}s
                <Input
                  type="number"
                  min={1}
                  max={Math.max(1, names.length)}
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
                  className="mt-1 font-bold"
                />
              </label>
            ) : (
              <label className="block text-sm font-medium">
                Members per {unitLabel}
                <Input
                  type="number"
                  min={1}
                  max={Math.max(1, names.length)}
                  value={size}
                  onChange={(e) => setSize(Math.max(1, Number(e.target.value)))}
                  className="mt-1 font-bold"
                />
              </label>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Odd number of students? The last pair automatically expands to 3 members.
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={generate} size="lg" className="w-full font-bold">
            <RotateCcw className="size-4 mr-2" />
            {groups.length ? "Shuffle & Regenerate" : `Generate ${unitLabel}s`}
          </Button>

          <ResetButton
            onClick={() => {
              setGroups([]);
              setError(null);
            }}
          />
        </div>

        {error && (
          <div className="mt-3">
            <EmptyHint>{error}</EmptyHint>
          </div>
        )}
      </Panel>

      {/* Main Results Display & Print View */}
      <div>
        {groups.length ? (
          <>
            {/* Toolbar for Copy, Print, Download */}
            <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Users className="size-4 text-primary" />
                <span>
                  {groups.length} {pairMode ? "Pairs" : `${unitLabel}s`} ({names.length} Total Students)
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CopyButton text={asText} label="Copy All" />
                <BrandedExportActions
                  title={`${pairMode ? "Pair" : unitLabel} Roster`}
                  content={asText}
                  filename={`picamini-${unitLabel.toLowerCase()}-roster.png`}
                  printHeader={false}
                  includePng={false}
                />

                <Button variant="outline" size="sm" onClick={downloadCsv}>
                  <FileSpreadsheet className="size-3.5 mr-1.5" /> Download CSV
                </Button>

                <Button variant="outline" size="sm" onClick={downloadTxt}>
                  <Download className="size-3.5 mr-1.5" /> Download TXT
                </Button>
              </div>
            </div>

            {/* PRINT ONLY PROFESSIONAL HEADER */}
            <div className="hidden print:block mb-8 text-center border-b pb-4">
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
                Class {pairMode ? "Pair" : unitLabel} Roster
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Generated on {new Date().toLocaleDateString()} • {names.length} Total Students • {groups.length} {unitLabel}s
              </p>
            </div>

            {/* Grid of Team Cards */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 print:grid-cols-2 print:gap-6">
              {groups.map((group, i) => (
                <div
                  key={i}
                  className="surface-card print-clean animate-reveal p-5 border rounded-2xl bg-card shadow-soft print:shadow-none print:border-slate-300 print:break-inside-avoid"
                >
                  <div className="flex items-center justify-between border-b pb-2.5 mb-3">
                    <h3 className="font-display text-base font-bold text-primary print:text-slate-900">
                      {pairMode ? "Pair" : unitLabel} {i + 1}
                    </h3>
                    <span className="rounded-full bg-secondary/80 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground print:bg-slate-100 print:text-slate-700">
                      {group.length} {group.length === 1 ? "Member" : "Members"}
                    </span>
                  </div>

                  <ol className="space-y-1.5 text-sm">
                    {group.map((name, idx) => (
                      <li
                        key={name}
                        className="flex items-center gap-2 rounded-lg bg-secondary/40 px-3 py-1.5 font-medium text-foreground print:bg-slate-50 print:border print:border-slate-200"
                      >
                        <span className="text-xs text-muted-foreground font-mono w-4">
                          {idx + 1}.
                        </span>
                        <span className="truncate">{name}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="surface-card grid min-h-[300px] place-items-center p-8 text-center text-muted-foreground rounded-2xl">
            <div className="flex flex-col items-center gap-2 max-w-sm">
              <Users className="size-10 text-muted-foreground/60" />
              <p className="font-semibold text-foreground">No teams generated yet</p>
              <p className="text-xs text-muted-foreground">
                Add student names in the setup panel and click Generate Teams to view the roster.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
